import { match, P } from 'ts-pattern';
import type { CMSText as CMSTextType } from '../schemas';
import { HTMLContent } from '@/components/ui/html-content';
import { cn } from '@/lib/utils';
import { CMSCTAButton } from '../cms-cta-button';
import { CMSInlineTextImage } from '../cms-inline-text-image';
import { CMSPlaceholder } from '../cms-placeholder';
import { CMSForm } from '../cms-form';
import React, { Suspense } from 'react';

const LazyCMSVideo = React.lazy(() => import('../cms-video'));

interface CMSTextProps {
  textData: CMSTextType;
  className?: string;
}

const CMSText = ({ textData, className }: CMSTextProps) => {
  return (
    <div
      data-component="CMSText"
      className={cn(
        // Margin for heading siblings
        '[&_:is(h1,h2,h3)+*]:mt-2 md:[&_:is(h1,h2,h3)+*]:mt-3 lg:[&_:is(h1,h2,h3)+*]:mt-4',
        // H1 text sizes
        '[&_h1]:text-3xl md:[&_h1]:text-4xl lg:[&_h1]:text-5xl',
        '[&_h1]:font-lateral1000',
        // H2 text sizes
        '[&_h2]:text-lg md:[&_h2]:text-2xl lg:[&_h2]:text-3xl',
        // H3 text sizes
        `[&_h3]:text-base md:[&_h3]:text-lg lg:[&_h3]:text-xl`,
        //H4 text sizes
        '[&_h4]:text-2xl md:[&_h4]:text-3xl lg:[&_h4]:text-4xl',
        `[&_h4]:font-lateral1000`,
        //Add text sizes for p tag that has no a tag children top level CMS text
        '[&_p]:text-sm md:[&_p]:text-base lg:[&_p]:text-lg',

        // Paragraph that has links (e.g CIS, Fact Sheet)
        // text sizes
        '[&_p_a]:underline [&_p_a]:text-black [&_p_a]:font-nunito500',

        // Font weight for heading tags
        '[&_:is(h2,h3)]:font-nunito700',

        // Add list disc for ul tags
        '[&_ul]:list-disc',

        // Add list decimal for ol tags
        '[&_ol]:list-decimal',

        '[&_p]:font-light',

        // Add bold for strong tag coming from CMS
        '[&_strong]:font-nunito700',

        // Add medium for strong tag inside p coming from CMS
        '[&_p_strong]:font-nunito700',

        // Add text size for p small
        `[&__p_small]:text-xs md:[&__p_small]:text-sm lg:[&_p_small]:text-base`,

        // Add margin inline auto for image inside h1,h2,h3 and p tag if parent tag has text align center
        '[&_h1[style*="text-align:_center"]_img]:mx-auto',
        '[&_h2[style*="text-align:_center"]_img]:mx-auto',
        '[&_h3[style*="text-align:_center"]_img]:mx-auto',
        '[&_p[style*="text-align:_center"]_img]:mx-auto',

        // Add default style for table
        '[&_table]:border [&_table]:border-deep-teal-50 [&_td]:border [&_td]:border-deep-teal-50 [&_td]:px-4',

        //Add style for <hr/>
        '[&_hr]:my-4 lg:[&_hr]:my-6',

        className
      )}
    >
      {match(textData)
        .with({ text: P.string, content: P.optional(undefined) }, (data) => (
          <HTMLContent content={data.text} />
        ))
        .with(
          {
            id: P.string,
            text: P.optional(undefined),
            content: P.array(),
          },
          (data) =>
            data.content.map((nestedComponent, index) =>
              match(nestedComponent)
                .with({ type: 'text', text: P.string }, (textNode) => (
                  <HTMLContent key={index} content={textNode.text} />
                ))
                .with({ type: 'cta' }, (data) => {
                  return (
                    <CMSCTAButton
                      key={index}
                      className={cn('mt-2 md:mt-4')}
                      ctaData={data}
                    />
                  );
                })
                .with({ type: 'video' } as const, (data) => (
                  <Suspense fallback={null} key={index}>
                    <LazyCMSVideo
                      data={data}
                      className={cn('rounded-[15px]')}
                    />
                  </Suspense>
                ))
                .with({ type: 'inline_text_image' } as const, (data) => (
                  <CMSInlineTextImage key={index} inlineTextImageData={data} />
                ))
                .with({ type: 'placeholder' } as const, (data) => (
                  <CMSPlaceholder key={index} placeholderData={data} />
                ))
                .with({ type: 'form' } as const, (formData) => (
                  <CMSForm formData={formData} key={index} />
                ))
                .otherwise(() => null)
            )
        )
        .otherwise(() => null)}
    </div>
  );
};
export { CMSText };
